import moment from "moment";
import useTableSort from "../../../hooks/useTableSort";
import {
  blockRackTableDataValidation,
  getTimeBasedOnUserTime,
  qualityStatusBadgeRenderer,
} from "../../../util/helper/helperFunctions";
import "../../../assets/css/BlockRack/blockRackModal.css";
import { memo, useEffect, useMemo, useState } from "react";
import useTableSearch from "../../../hooks/useTableSearch";
import SortableSearchableHeader from "../../Reusables/DataTable/SortableSearchableHeader";
import NoResult from "../../Reusables/DataTable/NoResult";

const columns = [
  { label: "Position", key: "palletPosition", enableSortSearch: false },
  { label: "Row", key: "row", enableSortSearch: false },
  { label: "Level", key: "palletLevel", enableSortSearch: false },
  { label: "LPN", key: "lpn", enableSortSearch: true },
  { label: "SKU", key: "sku", enableSortSearch: true },
  { label: "Lot", key: "lot", enableSortSearch: true },
  { label: "Quantity", key: "quantity", enableSortSearch: true },
  { label: "Quality Status", key: "qualityStatus", enableSortSearch: true },
  {
    label: "Storage Disabled",
    key: "positionStorageDisabled",
    enableSortSearch: true,
  },
  {
    label: "Storage Blocked",
    key: "positionStorageBlocked",
    enableSortSearch: true,
  },
  {
    label: "Retrieval Disabled",
    key: "positionRetrievalDisabled",
    enableSortSearch: true,
  },
  {
    label: "Retrieval Blocked",
    key: "positionRetrievalBlocked",
    enableSortSearch: true,
  },
  { label: "Production Date", key: "productionDate", enableSortSearch: true },
  { label: "Expiration Date", key: "expirationDate", enableSortSearch: true },
];

const transformIntoYesNo = (val) => (val ? "Yes" : "No");
const getFontClass = (val) =>
  val === "Yes" ? "error-red-font" : "success-green-font";

const transformBlockRackRow = ({
  lpn,
  sku,
  lot,
  quantity,
  qualityStatus,
  positionStorageDisabled,
  positionStorageBlocked,
  positionRetrievalDisabled,
  positionRetrievalBlocked,
  productionDate,
  expirationDate,
  palletPosition,
  row: rowPos,
  palletLevel,
  location,
  rackType,
}) => ({
  lpn: blockRackTableDataValidation(lpn),
  sku: blockRackTableDataValidation(sku),
  lot: blockRackTableDataValidation(lot),
  quantity: blockRackTableDataValidation(quantity),
  qualityStatus: blockRackTableDataValidation(qualityStatus),
  positionStorageDisabled: transformIntoYesNo(positionStorageDisabled),
  positionStorageBlocked: transformIntoYesNo(positionStorageBlocked),
  positionRetrievalDisabled: transformIntoYesNo(positionRetrievalDisabled),
  positionRetrievalBlocked: transformIntoYesNo(positionRetrievalBlocked),
  productionDate: !!blockRackTableDataValidation(productionDate)
    ? moment(getTimeBasedOnUserTime(productionDate)).format(
        "YYYY-MM-DD hh:mm:ss"
      )
    : "",
  expirationDate: !!blockRackTableDataValidation(expirationDate)
    ? moment(getTimeBasedOnUserTime(expirationDate)).format(
        "YYYY-MM-DD hh:mm:ss"
      )
    : "",
  palletPosition,
  row: blockRackTableDataValidation(rowPos),
  // palletLevel: blockRackTableDataValidation(palletLevel),
  // TODO: uncomment above and delete below line once palletLevel key is fixed for racks
  palletLevel: rackType ? Number(location.split(".").at(-1)) : palletLevel,
});

const BlockRackTable = ({
  locationData,
  updateBlockRackExportData,
  setClearFiltersFn,
  setShowClearFilter,
}) => {
  const [activeColumnSearch, setActiveColumnSearch] = useState(null);

  //Transforming raw data to viewing form to maintain data consistency for sort, searching and export
  const tableData = useMemo(() => {
    return locationData.map(transformBlockRackRow);
  }, [locationData]);

  /*
  Flow of param between hook : tableData => useTableSearch &&  filteredResult => useTableSort 
  Filtering first with tableData, then sorting with filteredData — regardless of user action order.
 */

  const { searchFilters, updateFilter, clearFilters, filteredData } =
    useTableSearch(tableData);
  const { requestSort, sortConfig, sortedData } = useTableSort(filteredData);

  useEffect(() => {
    updateBlockRackExportData(sortedData);
  }, [sortedData]);

  //passing to parent
  useEffect(() => {
    setClearFiltersFn(() => () => {
      clearFilters();
      requestSort(null);
    });
  }, [clearFilters, requestSort]);

  useEffect(() => {
    if (!tableData.length) return;

    const isSortOrSearchApplied =
      Object.values(searchFilters).some((val) => val) || sortConfig.key;
    setShowClearFilter(isSortOrSearchApplied);
  }, [searchFilters, sortConfig.key]);

  const RenderTableContent = () => {
    if (sortedData && sortedData.length !== 0) {
      return sortedData.map(
        (
          {
            lpn,
            sku,
            lot,
            quantity,
            qualityStatus,
            positionStorageDisabled,
            positionStorageBlocked,
            positionRetrievalDisabled,
            positionRetrievalBlocked,
            productionDate,
            expirationDate,
            palletPosition,
            row,
            palletLevel,
          },
          index
        ) => (
          <tr key={index}>
            <td className="block-table-data-block">{palletPosition}</td>
            <td className="block-table-data-block">{row}</td>
            <td className="block-table-data-block">{palletLevel}</td>
            <td className="block-table-data-block">{lpn}</td>
            <td className="block-table-data-block block-table-data-lg">
              {sku}
            </td>
            <td className="block-table-data-block block-table-data-md">
              {lot}
            </td>
            <td className="block-table-data-block">{quantity}</td>
            <td className="block-table-data-block">
              <div
                className={
                  !!blockRackTableDataValidation(qualityStatus)
                    ? `quality ${qualityStatusBadgeRenderer(
                        qualityStatus !== "" ? qualityStatus : "null"
                      )}`
                    : ""
                }
              >
                {qualityStatus}
              </div>
            </td>
            <td className="block-table-data-block">
              <h5 className={`${getFontClass(positionStorageDisabled)}`}>
                {positionStorageDisabled}
              </h5>
            </td>
            <td className="block-table-data-block">
              <h5 className={`${getFontClass(positionStorageBlocked)}`}>
                {positionStorageBlocked}
              </h5>
            </td>
            <td className="block-table-data-block">
              <h5 className={`${getFontClass(positionRetrievalDisabled)}`}>
                {positionRetrievalDisabled}
              </h5>
            </td>
            <td className="block-table-data-block">
              <h5
                data-testid="retreivalBlocked"
                className={`${getFontClass(positionRetrievalBlocked)}`}
              >
                {positionRetrievalBlocked}
              </h5>
            </td>
            <td className="block-table-data-block block-table-data-lg">
              {productionDate}
            </td>
            <td className="block-table-data-block block-table-data-lg">
              {expirationDate}
            </td>
          </tr>
        )
      );
    }
  };

  return (
    <table id="block-location-table-block-rack">
      <thead>
        <tr>
          {columns.map(({ label, key, enableSortSearch }, index) => (
            <SortableSearchableHeader
              key={index}
              index={index}
              field={key}
              label={label}
              sortConfig={sortConfig}
              searchFilters={searchFilters}
              activeColumnSearch={activeColumnSearch}
              setActiveColumnSearch={setActiveColumnSearch}
              enableSortSearch={enableSortSearch}
              requestSort={requestSort}
              updateFilter={updateFilter}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {!sortedData.length && tableData.length ? (
          <NoResult colSpan={columns.length} />
        ) : (
          <RenderTableContent />
        )}
      </tbody>
    </table>
  );
};

export default memo(BlockRackTable);
